<script setup>
import { ref } from 'vue'
import { useForm, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import VendorPortalLayout from '@/Layouts/VendorPortalLayout.vue'
import LocationPickerModal from '@/Components/UI/LocationPickerModal.vue'

defineOptions({ layout: VendorPortalLayout })

const { t } = useI18n()

const props = defineProps({
  tour:          { type: Object, required: true },
  errors:        { type: Object, default: () => ({}) },
  categories:    { type: Array,  default: () => [] },
  statuses:      { type: Array,  default: () => [] },
  vendor:        { type: Object, required: true },
  currency_code: { type: String, default: 'THB' },
  // Display-currency overrides — controller passes the enabled
  // non-canonical currencies + live FX rates so the form can render
  // auto-converted previews next to each "Set own price" checkbox.
  display_currencies: { type: Array,  default: () => [] },
  fx_margin_pct:      { type: Number, default: 1.0 },
  fx_rates:           { type: Object, default: () => ({}) }
})

const FORMATS = {
  THB: { symbol: '฿', decimals: 2 },
  USD: { symbol: '$', decimals: 2 },
  RUB: { symbol: '₽', decimals: 2 },
  CNY: { symbol: '¥', decimals: 2 }
}

const isEdit = !!props.tour.id

const form = useForm({
  title:                 props.tour.title ?? '',
  summary:               props.tour.summary ?? '',
  description:           props.tour.description ?? '',
  category:              props.tour.category ?? 'safari',
  duration_minutes:      props.tour.duration_minutes ?? 60,
  price_adult_cents:     props.tour.price_adult_cents ?? 0,
  price_child_cents:     props.tour.price_child_cents ?? 0,
  max_group_size:        props.tour.max_group_size ?? '',
  meeting_point_address:     props.tour.meeting_point_address ?? '',
  meeting_point_lat:         props.tour.meeting_point_lat ?? '',
  meeting_point_lng:         props.tour.meeting_point_lng ?? '',
  meeting_point_description: props.tour.meeting_point_description ?? '',
  inclusions:            props.tour.inclusions ?? '',
  languages_offered:     props.tour.languages_offered ?? '',
  status:                props.tour.status ?? 'active',
  photos:                []
})

// ── Per-currency display-price overrides (same shape as Vehicle form) ──
const overrideState = ref({})
props.display_currencies.forEach(code => {
  const existing = (props.tour.tour_prices || []).find(p => p.currency_code === code)
  overrideState.value[code] = {
    id:                  existing?.id || null,
    adult_enabled:       existing && existing.price_adult_cents != null,
    child_enabled:       existing && existing.price_child_cents != null,
    price_adult_cents:   existing?.price_adult_cents ?? null,
    price_child_cents:   existing?.price_child_cents ?? null
  }
})

function autoConverted(field, code) {
  const rate = props.fx_rates[code]
  if (!rate) return null
  const canonicalCents = form[field] || 0
  const margin = 1 + (props.fx_margin_pct / 100.0)
  return Math.round(canonicalCents * rate * margin)
}

function formatCents(cents, code) {
  if (cents == null) return '—'
  const fmt = FORMATS[code] || { symbol: code, decimals: 2 }
  return `${fmt.symbol}${(cents / 100).toFixed(fmt.decimals)}`
}

function inputCurrencyFromCents(cents) {
  if (cents == null) return ''
  return (cents / 100).toString()
}

function inputCurrencyToCents(input) {
  const num = parseFloat(input)
  if (Number.isNaN(num)) return null
  return Math.round(num * 100)
}

function buildFormData() {
  const fd = new FormData()
  const fields = ['title', 'summary', 'description', 'category',
                  'duration_minutes', 'price_adult_cents', 'price_child_cents',
                  'max_group_size', 'meeting_point_address',
                  'meeting_point_lat', 'meeting_point_lng',
                  'meeting_point_description',
                  'inclusions', 'languages_offered', 'status']
  fields.forEach(k => fd.append(`tour[${k}]`, form[k] ?? ''))
  form.photos.forEach(file => fd.append('tour[photos][]', file))

  // Per-currency override rows — same shape as Vehicle form.
  props.display_currencies.forEach((code, idx) => {
    const s = overrideState.value[code]
    if (!s) return
    const hasAnyEnabled = s.adult_enabled || s.child_enabled
    if (s.id && !hasAnyEnabled) {
      fd.append(`tour[tour_prices_attributes][${idx}][id]`, s.id)
      fd.append(`tour[tour_prices_attributes][${idx}][_destroy]`, '1')
    } else if (hasAnyEnabled) {
      if (s.id) fd.append(`tour[tour_prices_attributes][${idx}][id]`, s.id)
      fd.append(`tour[tour_prices_attributes][${idx}][currency_code]`, code)
      fd.append(`tour[tour_prices_attributes][${idx}][price_adult_cents]`,
                s.adult_enabled && s.price_adult_cents != null ? s.price_adult_cents : '')
      fd.append(`tour[tour_prices_attributes][${idx}][price_child_cents]`,
                s.child_enabled && s.price_child_cents != null ? s.price_child_cents : '')
    }
  })

  return fd
}

function submit() {
  const fd = buildFormData()
  const opts = { onError: (errors) => { form.errors = errors }, preserveScroll: true }
  if (isEdit) {
    fd.append('_method', 'PATCH')
    router.post(`/tours/${props.tour.id}`, fd, opts)
  } else {
    router.post('/tours', fd, opts)
  }
}

function onPhotos(e) {
  form.photos = Array.from(e.target.files || [])
}

function err(field) {
  return form.errors[field] || props.errors[field]?.[0]
}

function centsToInput(cents) {
  if (cents == null) return ''
  const v = cents / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

const adultPriceDollars = {
  get: () => centsToInput(form.price_adult_cents),
  set: (v) => { form.price_adult_cents = Math.round(parseFloat(v) * 100) || 0 }
}
const childPriceDollars = {
  get: () => centsToInput(form.price_child_cents),
  set: (v) => { form.price_child_cents = Math.round(parseFloat(v) * 100) || 0 }
}

const inputBase = 'w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
const errorCls  = 'mt-1 text-xs text-[color:var(--danger)]'

// Meeting-point map picker — shared LocationPickerModal (Leaflet +
// Nominatim) drops a pin, reverse-geocodes, and emits { address, lat,
// lng } on save. We persist all three onto the tour record.
const meetingPickerOpen = ref(false)
function onMeetingLocationSaved(payload) {
  form.meeting_point_address = payload.address ?? form.meeting_point_address
  form.meeting_point_lat     = payload.lat     ?? form.meeting_point_lat
  form.meeting_point_lng     = payload.lng     ?? form.meeting_point_lng
  meetingPickerOpen.value = false
}
function clearMeetingLocation() {
  form.meeting_point_address = ''
  form.meeting_point_lat     = ''
  form.meeting_point_lng     = ''
}
</script>

<template>
  <Link
    :href="isEdit ? `/tours/${tour.id}` : '/'"
    class="back"
  >
    ← {{ t('vendor_portal.tour_form.back') }}
  </Link>
  <h1 class="title">
    {{ isEdit ? t('vendor_portal.tour_form.title_edit') : t('vendor_portal.tour_form.title') }}
  </h1>
  <p class="subtitle">
    {{ t('vendor_portal.tour_form.subtitle', { vendor: vendor.name }) }}
  </p>

  <div
    v-if="tour.approval_state"
    class="approval-pill"
    :class="`approval-pill--${tour.approval_state}`"
  >
    {{ t(`vendor_portal.tour.approval.${tour.approval_state}`) }}
    <span
      v-if="tour.rejection_reason"
      class="approval-pill__reason"
    >— {{ tour.rejection_reason }}</span>
  </div>

  <form
    id="tour-form"
    class="card"
    @submit.prevent="submit"
  >
    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.title') }} *</label>
      <input
        v-model="form.title"
        type="text"
        required
        :class="[inputBase, err('title') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
      >
      <p
        v-if="err('title')"
        :class="errorCls"
      >
        {{ err('title') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.summary') }}</label>
      <input
        v-model="form.summary"
        type="text"
        maxlength="200"
        :class="[inputBase, 'border-[color:var(--line)]']"
      >
      <p class="hint">
        {{ t('vendor_portal.tour_form.summary_hint') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.description') }}</label>
      <textarea
        v-model="form.description"
        rows="5"
        :class="[inputBase, 'border-[color:var(--line)]']"
      />
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.category') }}</label>
        <select
          v-model="form.category"
          :class="[inputBase, 'border-[color:var(--line)] capitalize']"
        >
          <option
            v-for="c in categories"
            :key="c"
            :value="c"
          >
            {{ t(`vendor_portal.tour.categories.${c}`) }}
          </option>
        </select>
      </div>
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.duration_minutes') }} *</label>
        <input
          v-model.number="form.duration_minutes"
          type="number"
          min="1"
          required
          :class="[inputBase, err('duration_minutes') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
        >
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.price_adult') }} ({{ currency_code }}) *</label>
        <input
          :value="adultPriceDollars.get()"
          type="number"
          min="0"
          step="1"
          required
          :class="[inputBase, 'border-[color:var(--line)]']"
          @input="adultPriceDollars.set($event.target.value)"
        >
      </div>
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.price_child') }} ({{ currency_code }})</label>
        <input
          :value="childPriceDollars.get()"
          type="number"
          min="0"
          step="1"
          :class="[inputBase, 'border-[color:var(--line)]']"
          @input="childPriceDollars.set($event.target.value)"
        >
        <p class="hint">
          {{ t('vendor_portal.tour_form.price_child_hint') }}
        </p>
      </div>
    </div>

    <!-- Per-currency display-price overrides — public catalog only -->
    <div v-if="display_currencies.length > 0" class="override-card">
      <h3 class="override-title">{{ t('vendor_portal.tour_form.fields.display_overrides') }}</h3>
      <p class="hint">{{ t('vendor_portal.tour_form.fields.display_overrides_hint') }}</p>
      <div v-for="code in display_currencies" :key="code" class="override-row">
        <div class="override-code">{{ code }}</div>
        <div class="grid2">
          <div>
            <label class="override-toggle">
              <input
                type="checkbox"
                :checked="overrideState[code]?.adult_enabled"
                @change="overrideState[code].adult_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].price_adult_cents = null"
              >
              <span>{{ t('vendor_portal.tour_form.fields.set_own_adult') }}</span>
            </label>
            <div v-if="overrideState[code]?.adult_enabled">
              <input
                type="number" min="0" step="1"
                :value="inputCurrencyFromCents(overrideState[code].price_adult_cents)"
                @input="overrideState[code].price_adult_cents = inputCurrencyToCents($event.target.value)"
                :class="[inputBase, 'border-[color:var(--line)]']"
                :placeholder="`${FORMATS[code]?.symbol || ''}…`"
              >
            </div>
            <div v-else class="override-preview">
              {{ t('vendor_portal.tour_form.fields.auto_converted') }}: {{ formatCents(autoConverted('price_adult_cents', code), code) }}
            </div>
          </div>
          <div>
            <label class="override-toggle">
              <input
                type="checkbox"
                :checked="overrideState[code]?.child_enabled"
                @change="overrideState[code].child_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].price_child_cents = null"
              >
              <span>{{ t('vendor_portal.tour_form.fields.set_own_child') }}</span>
            </label>
            <div v-if="overrideState[code]?.child_enabled">
              <input
                type="number" min="0" step="1"
                :value="inputCurrencyFromCents(overrideState[code].price_child_cents)"
                @input="overrideState[code].price_child_cents = inputCurrencyToCents($event.target.value)"
                :class="[inputBase, 'border-[color:var(--line)]']"
                :placeholder="`${FORMATS[code]?.symbol || ''}…`"
              >
            </div>
            <div v-else class="override-preview">
              {{ t('vendor_portal.tour_form.fields.auto_converted') }}: {{ formatCents(autoConverted('price_child_cents', code), code) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.max_group_size') }}</label>
        <input
          v-model.number="form.max_group_size"
          type="number"
          min="1"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
      <div>
        <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.languages_offered') }}</label>
        <input
          v-model="form.languages_offered"
          type="text"
          placeholder="en,ru,th"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
        <p class="hint">
          {{ t('vendor_portal.tour_form.languages_hint') }}
        </p>
      </div>
    </div>

    <div class="meeting-card">
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.meeting_point') }}</label>
      <div class="meeting-row">
        <div class="meeting-row__main">
          <p v-if="form.meeting_point_address" class="meeting-address">
            {{ form.meeting_point_address }}
          </p>
          <p v-else class="meeting-empty">
            {{ t('vendor_portal.tour_form.meeting_point_empty') }}
          </p>
          <p v-if="form.meeting_point_lat && form.meeting_point_lng" class="meeting-coords">
            {{ Number(form.meeting_point_lat).toFixed(5) }}, {{ Number(form.meeting_point_lng).toFixed(5) }}
          </p>
        </div>
        <div class="meeting-actions">
          <button type="button" class="btn" @click="meetingPickerOpen = true">
            {{ form.meeting_point_address ? t('vendor_portal.tour_form.meeting_point_edit') : t('vendor_portal.tour_form.meeting_point_set') }}
          </button>
          <button v-if="form.meeting_point_address" type="button" class="btn btn--ghost" @click="clearMeetingLocation">
            {{ t('vendor_portal.tour_form.meeting_point_clear') }}
          </button>
        </div>
      </div>
      <label :class="[labelCls, 'mt-3']">
        {{ t('vendor_portal.tour_form.fields.meeting_point_description') }}
      </label>
      <textarea
        v-model="form.meeting_point_description"
        rows="3"
        :placeholder="t('vendor_portal.tour_form.meeting_point_description_placeholder')"
        :class="[inputBase, 'border-[color:var(--line)]']"
      ></textarea>
      <p class="hint">{{ t('vendor_portal.tour_form.meeting_point_description_hint') }}</p>
    </div>

    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.inclusions') }}</label>
      <textarea
        v-model="form.inclusions"
        rows="3"
        :placeholder="t('vendor_portal.tour_form.inclusions_placeholder')"
        :class="[inputBase, 'border-[color:var(--line)]']"
      />
    </div>

    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.photos') }}</label>
      <input
        type="file"
        accept="image/*"
        multiple
        class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
        @change="onPhotos"
      >
      <p class="hint">
        {{ t('vendor_portal.tour_form.photos_hint') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('vendor_portal.tour_form.fields.status') }}</label>
      <select
        v-model="form.status"
        :class="[inputBase, 'border-[color:var(--line)]']"
      >
        <option
          v-for="s in statuses"
          :key="s"
          :value="s"
        >
          {{ t(`vendor_portal.tour.status.${s}`) }}
        </option>
      </select>
    </div>

    <div
      v-if="errors.base"
      class="errors"
    >
      <p
        v-for="(m, i) in (Array.isArray(errors.base) ? errors.base : [errors.base])"
        :key="i"
      >
        {{ m }}
      </p>
    </div>

    <div class="actions">
      <Link
        href="/"
        class="btn"
      >
        {{ t('vendor_portal.tour_form.cancel') }}
      </Link>
      <button
        type="submit"
        class="btn btn--primary"
        :disabled="form.processing"
      >
        {{ form.processing ? t('vendor_portal.tour_form.submitting') : t('vendor_portal.tour_form.submit') }}
      </button>
    </div>
  </form>

  <LocationPickerModal
    :open="meetingPickerOpen"
    :title="t('vendor_portal.tour_form.meeting_point_modal_title')"
    :address="form.meeting_point_address"
    :lat="form.meeting_point_lat || null"
    :lng="form.meeting_point_lng || null"
    @save="onMeetingLocationSaved"
    @close="meetingPickerOpen = false"
  />
</template>

<style scoped>
.back     { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.title    { font-family: var(--font-display, var(--display)); font-size: 1.5rem; color: var(--ink); margin: 0.5rem 0 0.25rem; }
.subtitle { color: var(--inkSoft); margin: 0 0 1rem; font-size: 0.9375rem; }
.approval-pill { display: inline-block; padding: 0.4rem 0.75rem; margin: 0 0 1rem; border-radius: 999px; font-size: 0.8125rem; font-weight: 500; }
.approval-pill--pending  { background: color-mix(in oklch, var(--warn) 16%, var(--surface)); color: var(--warn); }
.approval-pill--approved { background: color-mix(in oklch, var(--ok) 16%, var(--surface)); color: var(--ok); }
.approval-pill--rejected { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.approval-pill__reason   { font-weight: 400; }

.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1.25rem 1.5rem; display: grid; gap: 1.25rem; max-width: 48rem; }
.grid2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 640px) { .grid2 { grid-template-columns: 1fr 1fr; } }
.hint { margin: 0.25rem 0 0; font-size: 0.75rem; color: var(--muted); }
.errors { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); padding: 0.75rem 1rem; border-radius: 0.5rem; font-size: 0.875rem; }
.errors p { margin: 0; }
.actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
.btn { padding: 0.55rem 1rem; border: 1px solid var(--line); background: var(--surface); color: var(--ink); border-radius: var(--radius-button, 8px); text-decoration: none; font-weight: 500; cursor: pointer; }
.btn--primary { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }
.override-card {
  border: 1px solid var(--line); border-radius: 0.75rem;
  padding: 1rem 1.25rem; display: grid; gap: 1rem;
  background: var(--surface);
}
.override-title { margin: 0; font-size: 0.875rem; font-weight: 600; color: var(--ink); }
.override-row { padding-top: 0.75rem; border-top: 1px solid var(--line); display: grid; gap: 0.5rem; }
.override-row:first-of-type { padding-top: 0; border-top: 0; }
.override-code { font-size: 0.875rem; font-weight: 600; color: var(--inkSoft); }
.override-toggle { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8125rem; color: var(--inkSoft); margin-bottom: 0.25rem; cursor: pointer; }
.override-preview { font-size: 0.75rem; color: var(--muted); font-style: italic; padding-top: 0.25rem; }
.meeting-card {
  border: 1px solid var(--line); border-radius: 0.5rem;
  padding: 0.75rem 0.875rem; background: var(--surface);
}
.meeting-row { display: flex; align-items: flex-start; gap: 0.75rem; }
.meeting-row__main { flex: 1; min-width: 0; }
.meeting-address { margin: 0; color: var(--ink); font-size: 0.9375rem; }
.meeting-coords { margin: 0.2rem 0 0; color: var(--muted); font-size: 0.75rem; font-family: var(--font-mono, monospace); }
.meeting-empty { margin: 0; color: var(--muted); font-size: 0.875rem; font-style: italic; }
.meeting-actions { display: flex; flex-direction: column; gap: 0.35rem; align-items: stretch; min-width: 8rem; }
.btn--ghost { background: transparent; border-color: var(--line); color: var(--muted); }
.btn--ghost:hover { color: var(--ink); }
.mt-3 { margin-top: 0.75rem; display: inline-block; }
</style>
